import { useState, useEffect } from 'react';
import api from './api';
import {
  RED, DEPT_FULL, DEPT_COLOR,
  Avatar, StatusBadge, ExpiryBadge, ServerFileLink, PSection, PRow,
  EditModal, EditForm,
  docExpiryStatus, formatDate, isRegistrationIncomplete, flaggedDocuments,
} from './FreelancersPage';
import { useTasks } from './TasksContext';
import { TaskPanel } from './TasksPage';

// ─── FreelancerCardPage ─────────────────────────────────────────────────────────
// Повноекранна картка фрілансера — відкривається в окремій вкладці браузера
// (посилання "↗ Open in new tab" з FreelancersPage), тому підвантажує дані сама
// через GET /api/freelancers/:id замість того, щоб отримувати їх пропсами.
export default function FreelancerCardPage({ freelancerId, currentUser, allUsers = [], projects = [] }) {
  const [freelancer, setFreelancer] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [editModal,  setEditModal]  = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleted,    setDeleted]    = useState(false);
  const [docUploading, setDocUploading] = useState({});
  const [taskOpen,   setTaskOpen]   = useState(false);
  const { addTask } = useTasks();

  const isAdmin    = ['admin', 'Admin', 'SuperAdmin', 'HR', 'PM'].includes(currentUser?.role);
  const canFinance = ['Admin', 'SuperAdmin', 'Finance'].includes(currentUser?.role);

  const load = () => {
    setLoading(true);
    setError('');
    api.freelancers.get(freelancerId)
      .then(res => setFreelancer(res.freelancer))
      .catch(e => setError(e.message || 'Failed to load freelancer'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [freelancerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    try { await api.freelancers.setStatus(freelancer.id, 'active'); load(); } catch (e) { alert(e.message); }
  };
  const block = async () => {
    try { await api.freelancers.setStatus(freelancer.id, 'blocked'); load(); } catch (e) { alert(e.message); }
  };
  const remind = async () => {
    try { await api.freelancers.remind(freelancer.id); alert('Reminder email sent.'); }
    catch (e) { alert('Failed to send reminder: ' + e.message); }
  };
  const invite = async () => {
    try { await api.freelancers.invite(freelancer.id); alert('Invitation email sent.'); load(); }
    catch (e) { alert('Failed to send invitation: ' + e.message); }
  };
  const flagDoc = async (category, docId, reason) => {
    await api.freelancers.flagDocument(freelancer.id, docId, reason); // кидає при помилці — модалка сама покаже
    load();
  };
  const notifyFlagged = async () => {
    try {
      const res = await api.freelancers.notifyFlaggedDocuments(freelancer.id);
      alert(`Notified about ${res.count} flagged document${res.count === 1 ? '' : 's'}.`);
    } catch (e) { alert('Failed to send notification: ' + e.message); }
  };
  const saveTask = async form => {
    try { await addTask(form); }
    catch (e) { alert('Failed to create task: ' + e.message); }
  };
  const remove = async () => {
    try {
      await api.freelancers.delete(freelancer.id);
      setDeleted(true);
      setConfirmDel(false);
    } catch (e) { alert('Failed to delete: ' + e.message); }
  };
  const save = async nf => {
    try {
      await api.freelancers.update(nf.id, nf);
      setEditModal(false);
      load();
    } catch (e) { alert('Failed to save: ' + e.message); }
  };
  const uploadDoc = async (category, fileList, extra = {}) => {
    setDocUploading(u => ({ ...u, [category]: true }));
    try {
      await api.freelancers.uploadDocuments(freelancer.id, category, fileList, extra);
      load();
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setDocUploading(u => ({ ...u, [category]: false })); }
  };
  const removeDoc = async (category, docId) => {
    try {
      await api.freelancers.deleteDocument(freelancer.id, docId);
      load();
    } catch (e) { alert('Failed to remove file: ' + e.message); }
  };

  const wrap = { minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' };

  if (deleted) {
    return (
      <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>🗑</div>
          <p style={{ fontSize: '14px' }}>Freelancer deleted. You can close this tab.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={wrap}><p style={{ color: 'var(--text-dim)', padding: '32px', fontSize: '13px' }}>Loading…</p></div>;
  }

  if (error || !freelancer) {
    return (
      <div style={wrap}>
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '14px 18px', borderRadius: '8px', margin: '32px', fontSize: '13px' }}>
          ⚠️ {error || 'Freelancer not found'}
        </div>
      </div>
    );
  }

  const f = freelancer;

  return (
    <div style={wrap}>
      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>FREELANCER CARD</span>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {f.status === 'pending' && (
              <button onClick={approve} style={{ background: 'var(--success-bg)', border: '1px solid var(--success-text)', color: 'var(--success-text)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>✓ Approve</button>
            )}
            {f.status === 'active' && (
              <button onClick={block} style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', color: 'var(--warning-text)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>⊘ Block</button>
            )}
            {f.status === 'blocked' && (
              <button onClick={approve} style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>↺ Unblock</button>
            )}
            {f.status === 'active' && isRegistrationIncomplete(f) && (
              <button onClick={remind} title="Send a reminder to finish registration"
                style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🔔 Remind</button>
            )}
            {flaggedDocuments(f).length > 0 && (
              <button onClick={notifyFlagged} title="Email the freelancer the list of flagged documents"
                style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: RED, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🚩 Notify ({flaggedDocuments(f).length})</button>
            )}
            {!f.userId && (
              <button onClick={invite} title={f.invitedAt ? 'Resend the portal invitation email' : 'Invite this freelancer to register and complete onboarding'}
                style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                ✉️ {f.invitedAt ? 'Resend invite' : 'Invite to portal'}
              </button>
            )}
            {f.userId && (
              <button onClick={() => setTaskOpen(true)} title="Assign a task to this freelancer"
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-body)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>📌 Task</button>
            )}
            <button onClick={() => setEditModal(true)}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-body)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              ✏️ Edit
            </button>
            <button onClick={() => setConfirmDel(true)}
              style={{ background: 'transparent', border: '1px solid #7f1d1d', color: RED, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              🗑 Delete
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '28px' }}>
          <Avatar name={f.name} size={64} />
          <div>
            <div style={{ fontWeight: 700, fontSize: '22px', color: 'var(--text-primary)' }}>{f.name}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-dim)', margin: '4px 0 8px' }}>{f.role} · {f.gender}</div>
            <StatusBadge status={f.status} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <PSection title="Professional">
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {f.departments?.map(d => (
                <span key={d} style={{ background: DEPT_COLOR[d]+'22', color: DEPT_COLOR[d], border: `1px solid ${DEPT_COLOR[d]}44`, borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 700 }}>{d} · {DEPT_FULL[d]}</span>
              ))}
            </div>
            {f.departments?.some(d => (f.departmentLevels?.[d] || []).length > 0) && (
              <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {f.departments.filter(d => (f.departmentLevels?.[d] || []).length > 0).map(d => (
                  <div key={d} style={{ fontSize: '12px' }}>
                    <span style={{ color: 'var(--text-dim)' }}>{d} level: </span>
                    <span style={{ color: 'var(--text-body)' }}>{(f.departmentLevels[d] || []).join(', ')}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {f.skills?.map(s => (
                <span key={s} style={{ background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: '4px', padding: '2px 7px', fontSize: '11px' }}>{s}</span>
              ))}
            </div>
          </PSection>

          <PSection title="Contact">
            <PRow label="Email"    value={f.email} />
            <PRow label="Phone"    value={f.phone} />
            <PRow label="WhatsApp" value={f.whatsapp} />
            <PRow label="Additional Phone" value={f.additionalPhone} />
            <PRow label="Travel Port" value={f.travelPort} />
            {f.address?.country && (
              <PRow label="Address" value={`${f.address.street}, ${f.address.city}, ${f.address.country} ${f.address.zip}`} />
            )}
          </PSection>

          <PSection title="Personal">
            <PRow label="Date of Birth"  value={formatDate(f.dob)} />
            <PRow label="Nationality"    value={f.nationality} />
            <PRow label="Passport №"     value={f.passportNumber} />
            {f.passportExpiry && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ color: 'var(--text-dim)' }}>Passport expiry</span>
                <span style={{ color: 'var(--text-body)' }}>{formatDate(f.passportExpiry)}<ExpiryBadge expiry={f.passportExpiry} /></span>
              </div>
            )}
            <PRow label="Marital Status" value={f.maritalStatus} />
            <PRow label="Place of Birth" value={[f.birthCity, f.birthCountry].filter(Boolean).join(', ')} />
          </PSection>

          {(f.additionalPassports||[]).length > 0 && (
            <PSection title="Additional Passports">
              {f.additionalPassports.map((p,i) => {
                const s = docExpiryStatus(p.expiry);
                return (
                  <div key={p.id||i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-body)' }}>📔 {p.number || '—'}</span>
                    <span style={{ color: s?.color || 'var(--text-muted)' }}>{p.expiry ? formatDate(p.expiry) : '—'}<ExpiryBadge expiry={p.expiry} /></span>
                  </div>
                );
              })}
            </PSection>
          )}

          {f.visas?.length > 0 && (
            <PSection title="Visas">
              {f.visas.map((v,i) => {
                const s = docExpiryStatus(v.expiry);
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-body)' }}>🛂 {v.country}</span>
                    <span style={{ color: s?.color || 'var(--text-muted)' }}>{formatDate(v.expiry)}<ExpiryBadge expiry={v.expiry} /></span>
                  </div>
                );
              })}
            </PSection>
          )}

          {(f.emergencyContact?.phone || f.emergencyContact?.email) && (
            <PSection title="Emergency Contact">
              <PRow label="Phone" value={f.emergencyContact.phone} />
              <PRow label="Email" value={f.emergencyContact.email} />
            </PSection>
          )}

          {canFinance && (f.bankAccounts||[]).length > 0 && (
            <PSection title="Financial">
              {f.bankAccounts.map((a,i) => (
                <div key={i} style={{ marginBottom: i < f.bankAccounts.length - 1 ? '12px' : 0, paddingBottom: i < f.bankAccounts.length - 1 ? '12px' : 0, borderBottom: i < f.bankAccounts.length - 1 ? '1px solid var(--bg-subtle)' : 'none' }}>
                  {f.bankAccounts.length > 1 && <div style={{ color: 'var(--text-dim)', fontSize: '11px', marginBottom: '4px' }}>Bank {i + 1}</div>}
                  <PRow label="Bank"     value={a.bankName} />
                  <PRow label="IBAN"     value={a.accountNumber} />
                  <PRow label="SWIFT"    value={a.swift} />
                  <PRow label="Holder"   value={a.accountHolderName} />
                  <PRow label="Currency" value={a.currency} />
                </div>
              ))}
            </PSection>
          )}
          {canFinance && !(f.bankAccounts||[]).length && f.finance?.bankName && (
            <PSection title="Financial">
              <PRow label="Bank"     value={f.finance.bankName} />
              <PRow label="IBAN"     value={f.finance.accountNumber} />
              <PRow label="SWIFT"    value={f.finance.swift} />
              <PRow label="Currency" value={f.finance.currency} />
            </PSection>
          )}

          {f.driveLink && (
            <PSection title="Cloud Storage">
              <a href={f.driveLink} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1a73e8', color: 'var(--text-primary)', borderRadius: '8px', padding: '8px 14px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
                📁 Open Google Drive
              </a>
            </PSection>
          )}

          {(f.projects||[]).length > 0 && (
            <PSection title="Projects">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {f.projects.map((p,i) => (
                  <div key={i} style={{ fontSize: '13px', color: 'var(--info-text)', background: 'var(--info-bg)', border: '1px solid #3b82f644', borderRadius: '6px', padding: '6px 10px' }}>📋 {p}</div>
                ))}
              </div>
            </PSection>
          )}
        </div>

        {/* Документи — на всю ширину */}
        <div style={{ marginTop: '24px' }}>
          <PSection title="Documents">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              {['passport', 'photo', 'cv'].map(cat => {
                const key = cat === 'passport' ? 'passportFiles' : cat === 'photo' ? 'photoFiles' : 'cvFiles';
                const label = cat === 'passport' ? 'Passport' : cat === 'photo' ? 'Photo' : 'CV';
                const files = f[key] || [];
                return (
                  <div key={cat}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{label}</span>
                      {isAdmin && (
                        <label style={{ color: 'var(--info-text)', fontSize: '11px', cursor: docUploading[cat] ? 'not-allowed' : 'pointer', opacity: docUploading[cat] ? 0.6 : 1 }}>
                          {docUploading[cat] ? 'Uploading…' : '+ Upload'}
                          <input type="file" style={{ display: 'none' }} disabled={docUploading[cat]}
                            onChange={e => { if (e.target.files?.length) uploadDoc(cat, e.target.files); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>
                    {files.length === 0
                      ? <div style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>Not uploaded</div>
                      : files.map(file => <ServerFileLink key={file.id} freelancerId={f.id} file={file} onRemove={isAdmin ? () => removeDoc(cat, file.id) : null} onFlag={isAdmin ? reason => flagDoc(cat, file.id, reason) : null} />)}
                  </div>
                );
              })}
            </div>
          </PSection>

          {(f.otherDocs||[]).length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <PSection title="Other Documents">
                {f.otherDocs.map((d,i) => (
                  <div key={d.id||i} style={{ marginBottom: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>📄 {d.title}</span>
                      {d.expiry && <ExpiryBadge expiry={d.expiry} />}
                    </div>
                    {(d.files||[]).length > 0 && (
                      <div style={{ marginTop: '6px' }}>
                        {d.files.map(file => <ServerFileLink key={file.id} freelancerId={f.id} file={file} onRemove={isAdmin ? () => removeDoc('other', file.id) : null} onFlag={isAdmin ? reason => flagDoc('other', file.id, reason) : null} />)}
                      </div>
                    )}
                  </div>
                ))}
              </PSection>
            </div>
          )}

          {(f.consentDocs||[]).length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <PSection title="Data Processing Consent">
                <div style={{ marginBottom: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>✍️ Signed electronically</span>
                    {f.consentSignedAt && (
                      <span style={{ color: 'var(--success-text)', fontSize: '11px' }}>{new Date(f.consentSignedAt).toLocaleString()}</span>
                    )}
                  </div>
                  {f.consentDocs.map(file => <ServerFileLink key={file.id} freelancerId={f.id} file={file} />)}
                </div>
              </PSection>
            </div>
          )}
        </div>

        {f.comment && (
          <div style={{ marginTop: '24px' }}>
            <PSection title="Admin Comment">
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                {f.comment}
              </div>
            </PSection>
          </div>
        )}

        {f.freelancerComment && (
          <div style={{ marginTop: '16px' }}>
            <PSection title="Freelancer Comment">
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                {f.freelancerComment}
              </div>
            </PSection>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <EditModal onClose={() => setEditModal(false)}>
          <EditForm initial={f} onSave={save} onCancel={() => setEditModal(false)} canFinance={canFinance} />
        </EditModal>
      )}

      {/* Delete Confirm */}
      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '32px', maxWidth: '400px', width: '90%', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗑</div>
            <h3 style={{ color: 'var(--text-primary)', margin: '0 0 8px', fontSize: '18px' }}>Delete freelancer?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 24px', lineHeight: 1.5 }}>
              Permanently delete <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>"{f.name}"</span>?<br />
              This action cannot be undone. Their login account is removed too, and they'll get an email about it.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setConfirmDel(false)} style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button onClick={remove} style={{ background: RED, border: 'none', color: 'var(--text-primary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Task Panel — призначення задачі цьому фрілансеру */}
      {taskOpen && (
        <TaskPanel
          onClose={() => setTaskOpen(false)}
          onSave={saveTask}
          projects={projects}
          allUsers={allUsers}
          initialAssignedTo={f.userId}
        />
      )}
    </div>
  );
}
